/**
 * Canonical execution semantics for every user-visible primary action.
 * Presentation layers must not invent handlers, self-links, or lifecycle state.
 */
import {
  CAPTURE_PRIMARY_ACTIONS,
  type UxPrimaryActionKind,
} from './next-action.ts';

export const UX_PRIMARY_ACTION_KINDS = [
  'EDIT_TERMS',
  'INVITE_BUYER',
  'CONFIRM_TERMS',
  'START_PACKING',
  'RECORD_SEAL',
  'ADD_SHIPMENT',
  'CONFIRM_HANDOFF',
  'RECORD_ARRIVAL',
  'RECORD_UNBOXING',
  'COMPLETE_TRANSACTION',
  'OPEN_PASSPORT',
  'AUTHORIZE_RETURN',
  'RECORD_RETURN_PACKING',
  'RECORD_RETURN_SEAL',
  'ADD_RETURN_SHIPMENT',
  'RECORD_RETURN_UNBOXING',
  'COMPLETE_RETURN',
] as const satisfies readonly UxPrimaryActionKind[];

type MissingPrimaryActionKind = Exclude<UxPrimaryActionKind, (typeof UX_PRIMARY_ACTION_KINDS)[number]>;
type ExtraPrimaryActionKind = Exclude<(typeof UX_PRIMARY_ACTION_KINDS)[number], UxPrimaryActionKind>;
const _assertAllPrimaryKindsListed: MissingPrimaryActionKind extends never ? ExtraPrimaryActionKind extends never ? true : never : never = true;
void _assertAllPrimaryKindsListed;

export type WorkflowActionCategory = 'COMMAND' | 'NAVIGATION' | 'NATIVE_HANDOFF';
export type WorkflowActionActor = 'seller' | 'buyer' | 'either';
export type PortalActionExecutionMode = 'COMMAND' | 'NAVIGATION' | 'NATIVE_HANDOFF' | 'MOBILE_ONLY';

export type PortalActionExecution =
  | {
    mode: 'COMMAND';
    command: 'CONFIRM_TERMS';
    method: 'POST';
    path: '/v1/consumer/transactions/:transactionId/confirm-terms';
  }
  | {
    mode: 'NAVIGATION';
    destination: 'PROOF';
    hrefTemplate: '/packproofs/:transactionId/proof';
  }
  | {
    mode: 'NATIVE_HANDOFF';
    hrefTemplate: '/packproofs/:transactionId/handoff';
  }
  | {
    mode: 'MOBILE_ONLY';
    reason: 'PORTAL_DOES_NOT_EXECUTE_THIS_WORKFLOW';
  };

export type WorkflowActionContractEntry = {
  category: WorkflowActionCategory;
  actor: WorkflowActionActor;
  portal: PortalActionExecution;
  mobile: WorkflowActionCategory;
  command?: {
    method: 'POST' | 'PUT';
    path: string;
  };
  navigation?: {
    mobilePath: string;
    portalPath?: string;
  };
};

export const UX_PRIMARY_ACTION_CONTRACT = {
  EDIT_TERMS: {
    category: 'NAVIGATION',
    actor: 'seller',
    portal: { mode: 'MOBILE_ONLY', reason: 'PORTAL_DOES_NOT_EXECUTE_THIS_WORKFLOW' },
    mobile: 'NAVIGATION',
    command: { method: 'PUT', path: '/v1/consumer/transactions/:transactionId' },
    navigation: { mobilePath: '/transaction/new' },
  },
  INVITE_BUYER: {
    category: 'COMMAND',
    actor: 'seller',
    portal: { mode: 'MOBILE_ONLY', reason: 'PORTAL_DOES_NOT_EXECUTE_THIS_WORKFLOW' },
    mobile: 'COMMAND',
    command: { method: 'POST', path: '/v1/consumer/transactions/:transactionId/invitations' },
  },
  CONFIRM_TERMS: {
    category: 'COMMAND',
    actor: 'either',
    portal: {
      mode: 'COMMAND',
      command: 'CONFIRM_TERMS',
      method: 'POST',
      path: '/v1/consumer/transactions/:transactionId/confirm-terms',
    },
    mobile: 'COMMAND',
    command: { method: 'POST', path: '/v1/consumer/transactions/:transactionId/confirm-terms' },
  },
  START_PACKING: {
    category: 'NATIVE_HANDOFF',
    actor: 'seller',
    portal: { mode: 'NATIVE_HANDOFF', hrefTemplate: '/packproofs/:transactionId/handoff' },
    mobile: 'NATIVE_HANDOFF',
    navigation: { mobilePath: '/pack/[id]', portalPath: '/packproofs/:transactionId/handoff' },
  },
  RECORD_SEAL: {
    category: 'NATIVE_HANDOFF',
    actor: 'seller',
    portal: { mode: 'NATIVE_HANDOFF', hrefTemplate: '/packproofs/:transactionId/handoff' },
    mobile: 'NATIVE_HANDOFF',
    navigation: { mobilePath: '/pack/[id]', portalPath: '/packproofs/:transactionId/handoff' },
  },
  ADD_SHIPMENT: {
    category: 'COMMAND',
    actor: 'seller',
    portal: { mode: 'MOBILE_ONLY', reason: 'PORTAL_DOES_NOT_EXECUTE_THIS_WORKFLOW' },
    mobile: 'COMMAND',
    command: { method: 'POST', path: '/v1/consumer/transactions/:transactionId/shipping' },
  },
  CONFIRM_HANDOFF: {
    category: 'COMMAND',
    actor: 'either',
    portal: { mode: 'MOBILE_ONLY', reason: 'PORTAL_DOES_NOT_EXECUTE_THIS_WORKFLOW' },
    mobile: 'COMMAND',
    command: { method: 'POST', path: '/v1/consumer/transactions/:transactionId/local-handoff' },
  },
  RECORD_ARRIVAL: {
    category: 'NATIVE_HANDOFF',
    actor: 'buyer',
    portal: { mode: 'NATIVE_HANDOFF', hrefTemplate: '/packproofs/:transactionId/handoff' },
    mobile: 'NATIVE_HANDOFF',
    navigation: { mobilePath: '/capture/[id]', portalPath: '/packproofs/:transactionId/handoff' },
  },
  RECORD_UNBOXING: {
    category: 'NATIVE_HANDOFF',
    actor: 'buyer',
    portal: { mode: 'NATIVE_HANDOFF', hrefTemplate: '/packproofs/:transactionId/handoff' },
    mobile: 'NATIVE_HANDOFF',
    navigation: { mobilePath: '/capture/[id]', portalPath: '/packproofs/:transactionId/handoff' },
  },
  COMPLETE_TRANSACTION: {
    category: 'COMMAND',
    actor: 'either',
    portal: { mode: 'MOBILE_ONLY', reason: 'PORTAL_DOES_NOT_EXECUTE_THIS_WORKFLOW' },
    mobile: 'COMMAND',
    command: { method: 'POST', path: '/v1/consumer/transactions/:transactionId/complete' },
  },
  OPEN_PASSPORT: {
    category: 'NAVIGATION',
    actor: 'either',
    portal: { mode: 'NAVIGATION', destination: 'PROOF', hrefTemplate: '/packproofs/:transactionId/proof' },
    mobile: 'NAVIGATION',
    navigation: { mobilePath: '/passport/[id]', portalPath: '/packproofs/:transactionId/proof' },
  },
  AUTHORIZE_RETURN: {
    category: 'COMMAND',
    actor: 'either',
    portal: { mode: 'MOBILE_ONLY', reason: 'PORTAL_DOES_NOT_EXECUTE_THIS_WORKFLOW' },
    mobile: 'COMMAND',
    command: { method: 'POST', path: '/v1/consumer/transactions/:id/returns/:returnPassportId/authorize' },
  },
  RECORD_RETURN_PACKING: {
    category: 'NATIVE_HANDOFF',
    actor: 'either',
    portal: { mode: 'NATIVE_HANDOFF', hrefTemplate: '/packproofs/:transactionId/handoff' },
    mobile: 'NATIVE_HANDOFF',
    navigation: { mobilePath: '/pack/[id]', portalPath: '/packproofs/:transactionId/handoff' },
  },
  RECORD_RETURN_SEAL: {
    category: 'NATIVE_HANDOFF',
    actor: 'either',
    portal: { mode: 'NATIVE_HANDOFF', hrefTemplate: '/packproofs/:transactionId/handoff' },
    mobile: 'NATIVE_HANDOFF',
    navigation: { mobilePath: '/pack/[id]', portalPath: '/packproofs/:transactionId/handoff' },
  },
  ADD_RETURN_SHIPMENT: {
    category: 'COMMAND',
    actor: 'either',
    portal: { mode: 'MOBILE_ONLY', reason: 'PORTAL_DOES_NOT_EXECUTE_THIS_WORKFLOW' },
    mobile: 'COMMAND',
    command: { method: 'POST', path: '/v1/consumer/transactions/:id/returns/:returnPassportId/shipping' },
  },
  RECORD_RETURN_UNBOXING: {
    category: 'NATIVE_HANDOFF',
    actor: 'either',
    portal: { mode: 'NATIVE_HANDOFF', hrefTemplate: '/packproofs/:transactionId/handoff' },
    mobile: 'NATIVE_HANDOFF',
    navigation: { mobilePath: '/capture/[id]', portalPath: '/packproofs/:transactionId/handoff' },
  },
  COMPLETE_RETURN: {
    category: 'COMMAND',
    actor: 'either',
    portal: { mode: 'MOBILE_ONLY', reason: 'PORTAL_DOES_NOT_EXECUTE_THIS_WORKFLOW' },
    mobile: 'COMMAND',
    command: { method: 'POST', path: '/v1/consumer/transactions/:id/returns/:returnPassportId/complete' },
  },
} as const satisfies Record<UxPrimaryActionKind, WorkflowActionContractEntry>;

export type PortalExecutableCommand = {
  [K in UxPrimaryActionKind]: (typeof UX_PRIMARY_ACTION_CONTRACT)[K]['portal'] extends { mode: 'COMMAND' }
    ? K
    : never
}[UxPrimaryActionKind];

export const PORTAL_EXECUTABLE_COMMANDS = UX_PRIMARY_ACTION_KINDS.filter(
  (kind): kind is PortalExecutableCommand => UX_PRIMARY_ACTION_CONTRACT[kind].portal.mode === 'COMMAND',
);

export const PORTAL_MOBILE_ONLY_NOTICE = 'Continue this step in the PackProof app.';

export function isUxPrimaryActionKind(value: string): value is UxPrimaryActionKind {
  return (UX_PRIMARY_ACTION_KINDS as readonly string[]).includes(value);
}

export function primaryActionContract(kind: UxPrimaryActionKind): (typeof UX_PRIMARY_ACTION_CONTRACT)[UxPrimaryActionKind] {
  return UX_PRIMARY_ACTION_CONTRACT[kind];
}

export function classifyPrimaryAction(kind: UxPrimaryActionKind): {
  category: WorkflowActionCategory;
  portalMode: PortalActionExecutionMode;
  kind: UxPrimaryActionKind;
};
export function classifyPrimaryAction(kind: UxPrimaryActionKind | null | undefined): {
  category: WorkflowActionCategory | 'WAIT';
  portalMode: PortalActionExecutionMode | 'WAIT';
  kind: UxPrimaryActionKind | null;
};
export function classifyPrimaryAction(kind: UxPrimaryActionKind | null | undefined): {
  category: WorkflowActionCategory | 'WAIT';
  portalMode: PortalActionExecutionMode | 'WAIT';
  kind: UxPrimaryActionKind | null;
} {
  if (!kind) {
    return { category: 'WAIT', portalMode: 'WAIT', kind: null };
  }
  if (!isUxPrimaryActionKind(kind)) {
    throw new Error(`Unknown PackProof primary action kind: ${String(kind)}`);
  }
  const entry = UX_PRIMARY_ACTION_CONTRACT[kind];
  return { category: entry.category, portalMode: entry.portal.mode, kind };
}

export function nativeHandoffActionKinds(): UxPrimaryActionKind[] {
  return UX_PRIMARY_ACTION_KINDS.filter((kind) => UX_PRIMARY_ACTION_CONTRACT[kind].category === 'NATIVE_HANDOFF');
}

export function captureContractMatchesEngine(): boolean {
  const fromContract = new Set(nativeHandoffActionKinds());
  if (fromContract.size !== CAPTURE_PRIMARY_ACTIONS.size) return false;
  for (const kind of CAPTURE_PRIMARY_ACTIONS) {
    if (!fromContract.has(kind)) return false;
  }
  return true;
}
